import {
  autocompleteClasses,
  Autocomplete,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Box,
  FormControl,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Collapse,
  Divider,
  TextField,
  Chip,
  Badge,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MuiColorInput } from 'mui-color-input';

import { useTheme, alpha } from '@mui/material/styles';
import { useCallback, useState } from 'react';

import { SGTooltip } from '../../../core';
import { useHiroCtx } from '../../HiroProvider';
import {
  HiroIndicator,
  genericNameFromSymbolLineParams,
  niceGenericNameFromSymbolLineParams,
  decodeSymbolLineParams,
} from 'types/hiro';
import {
  availableIndicators,
  indicatorFromName,
  hiroIndicatorValid,
  getConstructorInputs,
} from './AvailableIndicators';
import { Fx } from 'icons/Fx';

const INDICATOR_COLORS = [
  '#18bdac',
  '#6467E8',
  '#7c94eb',
  '#cb7b27',
  '#22CC63',
  '#FF8FC0',
  '#F9F871',
  '#22e3ec',
];

export const IndicatorsButton = () => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [anchorSettingsEl, setAnchorSettingsEl] = useState<HTMLElement | null>(
    null,
  );
  const theme = useTheme();
  const { hiroIndicators, update, hiroSelectedSeriesList } = useHiroCtx();
  const setHiroIndicators = useCallback(
    (v: typeof hiroIndicators) => update({ hiroIndicators: v }),
    [update],
  );
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const validCount = hiroIndicators.filter(hiroIndicatorValid).length;

  const closeIndicatorsPopover = useCallback(() => {
    setPopupOpen(false);
    const updatedIndicators = hiroIndicators.filter(hiroIndicatorValid);
    setHiroIndicators(updatedIndicators);
    setExpandedIdx(null);
  }, [hiroIndicators, setHiroIndicators]);

  const handleAddIndicator = useCallback(() => {
    setHiroIndicators([
      {
        seriesName: hiroSelectedSeriesList?.[0]?.seriesName,
        symbolLineParams: hiroSelectedSeriesList?.[0]?.symbolLineParams,
        colors: [
          INDICATOR_COLORS[hiroIndicators.length % INDICATOR_COLORS.length],
        ],
      },
      ...hiroIndicators,
    ]);
    setExpandedIdx(0);
  }, [
    hiroIndicators,
    hiroSelectedSeriesList,
    setHiroIndicators,
    setExpandedIdx,
  ]);

  return (
    <>
      <SGTooltip title="Technical Indicators" placement="top">
        <Badge
          badgeContent={validCount || undefined}
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: 9,
              height: 14,
              minWidth: 14,
              bgcolor: theme.palette.primary.main,
              color: '#000',
              fontWeight: 700,
              marginRight: '0.9em',
            },
          }}
        >
          <IconButton
            data-ph-capture-attribute-element="hiro-indicators-open"
            aria-label="Indicators"
            onClick={(event) => {
              setAnchorSettingsEl(event.currentTarget);
              const nextOpen = !popupOpen;
              if (nextOpen) {
                setPopupOpen(true);
                if (hiroIndicators.length === 0) {
                  handleAddIndicator();
                } else {
                  setExpandedIdx(null);
                }
              } else {
                closeIndicatorsPopover();
              }
            }}
            size="medium"
            sx={{
              color: theme.palette.primary.main,
              transition: 'color 0.15s',
              marginRight: '0.6em',
            }}
          >
            <Fx />
          </IconButton>
        </Badge>
      </SGTooltip>
      <Popper
        id="hiro-indicators-settings"
        open={popupOpen}
        anchorEl={anchorSettingsEl}
        placement="bottom-end"
        style={{ zIndex: 1_299 }}
      >
        <Box
          sx={{
            width: '400px',
            mt: '8px',
            backgroundColor: theme.palette.background.paper,
            borderRadius: '16px',
            boxShadow:
              '0 8px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '80vh',
          }}
          className="canvas-no-drag"
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '16px',
              py: '12px',
              background:
                'linear-gradient(135deg, rgba(24,189,172,0.07) 0%, transparent 60%)',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '8px',
                  bgcolor: 'rgba(24,189,172,0.1)',
                  border: '1px solid rgba(24,189,172,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: theme.palette.primary.main,
                  fontSize: 14,
                }}
              >
                <Fx />
              </Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'Satoshi Complete',
                  color: 'text.primary',
                  lineHeight: 1,
                }}
              >
                Indicators
              </Typography>
              {validCount > 0 && (
                <Chip
                  label={validCount}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    fontFamily: 'Satoshi Complete',
                    bgcolor: 'rgba(24,189,172,0.12)',
                    color: theme.palette.primary.main,
                    border: '1px solid rgba(24,189,172,0.25)',
                    '& .MuiChip-label': { px: '6px' },
                  }}
                />
              )}
            </Box>
            <IconButton
              data-ph-capture-attribute-element="hiro-indicators-dismiss"
              size="small"
              onClick={closeIndicatorsPopover}
              sx={{
                color: 'text.secondary',
                borderRadius: '6px',
                p: '4px',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'rgba(255,255,255,0.06)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Scrollable indicator list */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: '12px',
              py: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '2px',
              },
            }}
          >
            {hiroIndicators.length === 0 ? (
              <Box
                sx={{
                  py: 5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled',
                    fontSize: 22,
                  }}
                >
                  <Fx />
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: 'text.disabled',
                    fontFamily: 'SF Pro Display',
                  }}
                >
                  No indicators configured
                </Typography>
              </Box>
            ) : (
              hiroIndicators.map((hiroIndicator, idx) => (
                <IndicatorCard
                  key={idx}
                  hiroIndicator={hiroIndicator}
                  expanded={expandedIdx === idx}
                  onToggleExpand={() =>
                    setExpandedIdx(expandedIdx === idx ? null : idx)
                  }
                  updateHiroIndicator={(updated) => {
                    const update = [...hiroIndicators];
                    update.splice(idx, 1, updated);
                    setHiroIndicators(update);
                  }}
                />
              ))
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: '12px',
              py: '10px',
              borderTop: '1px solid rgba(255,255,255,0.06)',
              flexShrink: 0,
            }}
          >
            <Button
              startIcon={<AddIcon sx={{ fontSize: '15px !important' }} />}
              variant="outlined"
              fullWidth
              size="small"
              onClick={handleAddIndicator}
              sx={{
                borderColor: 'rgba(24,189,172,0.25)',
                color: theme.palette.primary.main,
                textTransform: 'none',
                fontFamily: 'Satoshi Complete',
                fontSize: 12,
                fontWeight: 500,
                py: '7px',
                borderRadius: '8px',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: 'rgba(24,189,172,0.06)',
                },
              }}
            >
              Add Indicator
            </Button>
          </Box>
          <Grid item sx={{ width: '100%', marginTop: 2 }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                float: 'right',
                marginRight: '2ex',
                marginBottom: '2ex',
                width: '45px',
                fontFamily: 'Satoshi Complete',
                fontSize: 12,
                textTransform: 'capitalize',
              }}
              onClick={closeIndicatorsPopover}
            >
              Close
            </Button>
          </Grid>
        </Box>
      </Popper>
    </>
  );
};

interface IndicatorCardProps {
  hiroIndicator: HiroIndicator;
  expanded: boolean;
  onToggleExpand: () => void;
  updateHiroIndicator: (indicator: HiroIndicator) => void;
}

const IndicatorCard = ({
  hiroIndicator,
  expanded,
  onToggleExpand,
  updateHiroIndicator,
}: IndicatorCardProps) => {
  const theme = useTheme();
  const isDeleting = hiroIndicator.deleting ?? false;
  const hasSeriesName = (hiroIndicator.seriesName?.length ?? 0) > 0;
  const hasIndicatorName = (hiroIndicator.indicatorName?.length ?? 0) > 0;
  const hasAnyName = hasSeriesName || hasIndicatorName;

  return (
    <Box
      sx={{
        borderRadius: '10px',
        border: `1px solid ${
          expanded ? 'rgba(24,189,172,0.35)' : 'rgba(255,255,255,0.07)'
        }`,
        overflow: 'hidden',
        transition: 'border-color 0.15s ease, background 0.15s ease',
        opacity: isDeleting ? 0.55 : 1,
        background: isDeleting
          ? 'rgba(255,94,94,0.03)'
          : expanded
            ? 'rgba(24,189,172,0.02)'
            : 'rgba(255,255,255,0.01)',
      }}
    >
      {/* Card header row */}
      <Box
        onClick={onToggleExpand}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          px: '12px',
          py: '9px',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { background: 'rgba(255,255,255,0.02)' },
        }}
      >
        {/* Color dot */}
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: isDeleting
              ? theme.palette.error.main
              : hiroIndicator.colors?.[0],
            flexShrink: 0,
            transition: 'background-color 0.15s',
          }}
        />

        {/* Label area */}
        <Box sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
          {hasAnyName ? (
            <Box>
              {hiroIndicator.symbolLineParams != null && (
                <Typography
                  component="div"
                  sx={{
                    fontSize: 12,
                    color: 'text.secondary',
                    fontFamily: 'SF Pro Display',
                    textDecoration: isDeleting ? 'line-through' : 'none',
                    lineHeight: 1.3,
                    mb: '1px',
                    mr: '4px',
                  }}
                >
                  {niceGenericNameFromSymbolLineParams(
                    hiroIndicator.symbolLineParams,
                  )}
                </Typography>
              )}
              <Typography
                component="div"
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: isDeleting ? 'text.disabled' : 'text.primary',
                  fontFamily: 'Satoshi Complete',
                  textDecoration: isDeleting ? 'line-through' : 'none',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {hasIndicatorName
                  ? hiroIndicator.indicatorName
                  : '— select indicator —'}
              </Typography>
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.disabled',
                fontFamily: 'Satoshi Complete',
                fontStyle: 'italic',
              }}
            >
              New Indicator
            </Typography>
          )}
        </Box>

        {/* Delete / restore toggle */}
        <IconButton
          data-ph-capture-attribute-element="hiro-indicator-delete-toggle"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            updateHiroIndicator({ ...hiroIndicator, deleting: !isDeleting });
          }}
          title={
            isDeleting ? 'Restore this indicator' : 'Remove this indicator'
          }
          sx={{
            color: isDeleting ? theme.palette.warning.main : 'text.secondary',
            borderRadius: '6px',
            p: '3px',
            flexShrink: 0,
            '&:hover': {
              color: isDeleting
                ? theme.palette.warning.main
                : theme.palette.error.main,
              bgcolor: isDeleting
                ? 'rgba(255,205,94,0.08)'
                : 'rgba(255,94,94,0.08)',
            },
          }}
        >
          {isDeleting ? (
            <UndoIcon sx={{ fontSize: 14 }} />
          ) : (
            <DeleteIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>

        {/* Expand arrow */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: expanded ? theme.palette.primary.main : 'text.secondary',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease, color 0.15s ease',
            flexShrink: 0,
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>

      {/* Expanded form */}
      <Collapse in={expanded} timeout={150}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
        <Box sx={{ p: '12px 14px 14px' }}>
          <IndicatorForm
            hiroIndicator={hiroIndicator}
            updateHiroIndicator={updateHiroIndicator}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

interface IndicatorProps {
  hiroIndicator: HiroIndicator;
  updateHiroIndicator: (hiroIndicator: HiroIndicator) => void;
}

const IndicatorForm = ({
  hiroIndicator,
  updateHiroIndicator,
}: IndicatorProps) => {
  const theme = useTheme();
  const { hiroSelectedSeriesList, sym } = useHiroCtx();
  const [descriptionPopupEl, setDescriptionPopupEl] =
    useState<null | HTMLElement>(null);

  const constructorInputMeta = getConstructorInputs(
    hiroIndicator.indicatorName!,
  );
  const isDeleting = hiroIndicator.deleting ?? false;

  const autocompleteSx = {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.12)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.2)',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.primary.main} !important`,
    },
  };

  const inputSx = {
    '& .MuiInputLabel-root': { fontSize: 12, fontFamily: 'SF Pro Display' },
    '& .MuiInputBase-input': { fontSize: 12, fontFamily: 'SF Pro Display' },
  };

  const DropdownPaper = (props: React.ComponentProps<typeof Paper>) => (
    <Paper
      {...props}
      style={{
        ...props.style,
        background: '#1C1F24',
        backgroundImage: 'none',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}
      sx={{
        zIndex: theme.zIndex.modal + 1,
        [`& .${autocompleteClasses.listbox}`]: {
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
        },
        [`& .${autocompleteClasses.option}`]: {
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          },
        },
      }}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Indicator dropdown + info button */}
      <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <Autocomplete
          size="small"
          sx={{ flex: 1, ...autocompleteSx }}
          disabled={isDeleting}
          options={availableIndicators.map((ind) => ind.name)}
          value={hiroIndicator.indicatorName ?? null}
          onChange={(_, indicatorName) => {
            if (indicatorName == null) {
              return;
            }
            const colors = indicatorFromName
              .get(indicatorName)
              ?.indicatorOutputs?.map((c) => c.defaultColor(theme));
            updateHiroIndicator({
              ...hiroIndicator,
              indicatorName,
              constructorInputs: undefined,
              ...(colors != null ? { colors } : {}),
            });
          }}
          renderInput={(params) => (
            <TextField {...params} label="Indicator" sx={inputSx} />
          )}
          PaperComponent={DropdownPaper}
        />

        {(hiroIndicator.indicatorName?.length ?? 0) > 0 && (
          <>
            <IconButton
              data-ph-capture-attribute-element="hiro-indicator-description"
              size="small"
              onClick={(e) =>
                setDescriptionPopupEl(e.currentTarget as HTMLElement)
              }
              sx={{
                color:
                  descriptionPopupEl != null
                    ? theme.palette.primary.main
                    : 'text.secondary',
                borderRadius: '6px',
                p: '5px',
                flexShrink: 0,
                '&:hover': {
                  color: theme.palette.primary.main,
                  bgcolor: 'rgba(24,189,172,0.08)',
                },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Popper
              anchorEl={descriptionPopupEl}
              open={descriptionPopupEl != null}
              style={{ zIndex: 1_299 }}
              placement="left-start"
            >
              <ClickAwayListener
                onClickAway={() => setDescriptionPopupEl(null)}
              >
                <Box
                  sx={{
                    maxWidth: '340px',
                    p: '16px 18px 18px',
                    bgcolor: '#1C1F24',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    '& p': {
                      margin: '0 0 8px 0',
                      fontSize: 12,
                      lineHeight: 1.65,
                      color: theme.palette.text.secondary,
                      '&:last-child': { mb: 0 },
                    },
                    '& ul': {
                      margin: '0 0 8px 0',
                      paddingLeft: '20px',
                      '& li': {
                        fontSize: 12,
                        lineHeight: 1.65,
                        color: theme.palette.text.secondary,
                        mb: '3px',
                      },
                    },
                    '& strong': {
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    },
                    '& code': {
                      bgcolor: 'rgba(255,255,255,0.06)',
                      px: '4px',
                      py: '1px',
                      borderRadius: '3px',
                      fontSize: 11,
                      fontFamily: 'monospace',
                    },
                    '& a': {
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: 'Satoshi Complete',
                      color: 'text.primary',
                      mb: '10px',
                      lineHeight: 1.3,
                    }}
                  >
                    {hiroIndicator.indicatorName}
                  </Typography>
                  {
                    indicatorFromName.get(hiroIndicator.indicatorName!)
                      ?.description
                  }
                </Box>
              </ClickAwayListener>
            </Popper>
          </>
        )}
      </Box>

      {/* Series dropdown */}
      <FormControl size="small" fullWidth>
        <Select
          sx={autocompleteSx}
          disabled={isDeleting}
          value={
            hiroIndicator.symbolLineParams != null
              ? genericNameFromSymbolLineParams(hiroIndicator.symbolLineParams)
              : ''
          }
          label="Series Kind"
          onChange={(evt) =>
            updateHiroIndicator({
              ...hiroIndicator,
              seriesName: evt.target.value,
              symbolLineParams: decodeSymbolLineParams(
                `${sym}  ${evt.target.value}`,
              ),
            })
          }
        >
          {hiroSelectedSeriesList.map(({ symbolLineParams }) => (
            <MenuItem
              key={genericNameFromSymbolLineParams(symbolLineParams)}
              value={genericNameFromSymbolLineParams(symbolLineParams)}
              sx={{ fontSize: 12, fontFamily: 'SF Pro Display' }}
            >
              {niceGenericNameFromSymbolLineParams(symbolLineParams)}
            </MenuItem>
          ))}
        </Select>
        <InputLabel sx={{ fontSize: 12, fontFamily: 'SF Pro Display' }}>
          Series
        </InputLabel>
      </FormControl>

      {/* Constructor parameter inputs */}
      {constructorInputMeta
        ?.filter(
          ({ paramName }) =>
            [
              'Smoothing Indicator',
              'Smoothing RSI',
              'Config',
              'Bollinger Bands Width (BBW)',
            ].indexOf(paramName) < 0,
        )
        .map(({ paramName }, idx) => (
          <TextField
            key={paramName}
            label={paramName.replace(
              'Interval',
              'Interval (number of candles)',
            )}
            size="small"
            type="number"
            disabled={isDeleting}
            error={
              (
                hiroIndicator.constructorInputs?.[idx] ??
                (constructorInputMeta[idx]?.defaultValue as string) ??
                ''
              ).length == 0 ||
              (constructorInputMeta[idx]?.validate?.(hiroIndicator)?.length ??
                0) > 0
            }
            helperText={constructorInputMeta[idx]?.validate?.(hiroIndicator)}
            value={
              hiroIndicator.constructorInputs?.[idx] ??
              (constructorInputMeta[idx]?.defaultValue as string) ??
              ''
            }
            onChange={(evt) =>
              updateHiroIndicator({
                ...hiroIndicator,
                constructorInputs: constructorInputMeta.map(
                  ({ defaultValue }, reqInputIdx) =>
                    idx === reqInputIdx
                      ? evt.target.value
                      : (hiroIndicator.constructorInputs?.[reqInputIdx] ??
                        defaultValue),
                ) as string[],
              })
            }
            sx={{
              '& .MuiInputLabel-root': {
                fontSize: 12,
                fontFamily: 'SF Pro Display',
              },
              '& .MuiInputBase-input': {
                fontSize: 12,
                fontFamily: 'SF Pro Display',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.12)',
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: 'rgba(255,255,255,0.2)',
                },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: theme.palette.primary.main,
                },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                {
                  display: 'none',
                },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            }}
          />
        ))}
      {(
        indicatorFromName.get(hiroIndicator.indicatorName ?? '')
          ?.indicatorOutputs ?? [null]
      ).map((indicatorOutput, colorIdx) => (
        <>
          {indicatorOutput != null && (
            <Typography>{indicatorOutput.name}</Typography>
          )}
          <MuiColorInput
            value={hiroIndicator.colors?.[colorIdx]}
            onChange={(color) =>
              updateHiroIndicator({
                ...hiroIndicator,
                colors: [
                  ...hiroIndicator.colors.slice(0, colorIdx),
                  color,
                  ...hiroIndicator.colors.slice(colorIdx + 1),
                ],
              })
            }
          />
        </>
      ))}
    </Box>
  );
};
